import { registerPlugin, Capacitor } from '@capacitor/core';

export interface OtaProgressEvent {
  status: 'downloading' | 'preparing' | 'permission_required' | 'ready' | 'error';
  percent?: number;
  bytesDownloaded?: number;
  totalBytes?: number;
  error?: string;
  message?: string;
}

export interface DownloadStateResult {
  status: string;
  percent: number;
  bytesDownloaded: number;
  totalBytes: number;
  error: string | null;
  isDownloading: boolean;
  isReady: boolean;
}

export interface OtaUpdaterPluginInterface {
  isNative(): Promise<{ isNative: boolean; platform: string; apiLevel: number }>;
  canInstallPackages(): Promise<{ canInstall: boolean }>;
  openInstallSettings(): Promise<{ opened: boolean }>;
  downloadAndInstall(options: { url: string }): Promise<{ ok: boolean; status: string; message?: string }>;
  getDownloadState(): Promise<DownloadStateResult>;
  installDownloadedApk(): Promise<{ ok: boolean; status: string; error?: string; message?: string }>;
  addListener(
    eventName: 'otaProgress',
    listenerFunc: (event: OtaProgressEvent) => void
  ): Promise<{ remove: () => void }>;
}

export const OtaUpdater = registerPlugin<OtaUpdaterPluginInterface>('OtaUpdater');

export function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

export async function canInstallUnknownApps(): Promise<boolean> {
  if (!isNativeAndroid()) return true;
  try {
    const res = await OtaUpdater.canInstallPackages();
    return res.canInstall;
  } catch {
    return true;
  }
}

export async function openUnknownAppSettings(): Promise<void> {
  if (!isNativeAndroid()) return;
  try {
    await OtaUpdater.openInstallSettings();
  } catch (err) {
    console.error('[OTA] Failed to open unknown app settings:', err);
  }
}

export async function getOtaDownloadState(): Promise<DownloadStateResult | null> {
  if (!isNativeAndroid()) return null;
  try {
    return await OtaUpdater.getDownloadState();
  } catch (err) {
    console.warn('[OTA] Failed to query native download state:', err);
    return null;
  }
}

export async function installDownloadedApk(): Promise<{ ok: boolean; status: string; error?: string }> {
  if (!isNativeAndroid()) {
    return { ok: false, status: 'not_native', error: 'Only supported on native Android' };
  }
  try {
    return await OtaUpdater.installDownloadedApk();
  } catch (err: any) {
    console.error('[OTA] installDownloadedApk failed:', err);
    return { ok: false, status: 'error', error: err?.message || 'Installer launch failed' };
  }
}

export async function triggerInAppOtaUpdate(
  apkUrl: string,
  onProgress?: (progress: OtaProgressEvent) => void
): Promise<{ ok: boolean; status: string; error?: string }> {
  if (!isNativeAndroid()) {
    // Web / PWA Fallback: trigger direct file download
    try {
      const a = document.createElement('a');
      a.href = apkUrl;
      a.download = 'fissal-mobile.apk';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return { ok: true, status: 'web_download_started' };
    } catch (err: any) {
      return { ok: false, status: 'error', error: err?.message || 'Failed to start download' };
    }
  }

  let progressSub: { remove: () => void } | null = null;
  if (onProgress) {
    try {
      progressSub = await OtaUpdater.addListener('otaProgress', (event) => {
        onProgress(event);
      });
    } catch (e) {
      console.warn('[OTA] Progress listener failed to attach:', e);
    }
  }

  try {
    const result = await OtaUpdater.downloadAndInstall({ url: apkUrl });
    return result;
  } catch (err: any) {
    console.error('[OTA] downloadAndInstall threw:', err);
    return { ok: false, status: 'error', error: err?.message || 'Update failed' };
  } finally {
    // Delay listener cleanup to allow trailing events
    setTimeout(() => {
      progressSub?.remove();
    }, 15000);
  }
}
